const controllerDeleteMessage = (tag, t?: (key: string, vars?: Record<string, string>) => string) => {
  if (t) {
    return t('delete.controllerMessage', { tag });
  }
  return `Controller ${tag} will be deleted. All circuits under ${tag} will also be deleted.`;
};
const circuitDeleteMessage = (tag, t?: (key: string, vars?: Record<string, string>) => string) => {
  if (t) {
    return t('delete.circuitMessage', { tag });
  }
  return `Circuit ${tag} will be deleted.`;
};
const commLoopDeleteMessage = (tag, t?: (key: string, vars?: Record<string, string>) => string) => {
  if (t) {
    return t('delete.commLoopMessage', { tag });
  }
  return `Devices attached to ${tag} will no longer be monitored.`;
};
// keys as lang look up in properties

export const deviceTypeConfig = {
  circuit: {
    title: 'deviceType.circuit',
    sectionFeatureFence: {
      additionalData: 'eht-process-data',
      designedSettings: 'programming-audit'
    },
    properties: {
      EHTInformation: {
        circuitTag: { selector: ['tag'] },
        controllerTag: { selector: ['parent_tag'], overrideElement: 'deviceLink' },
        model: { selector: ['model'], overrideElement: 'readOnly'  },
        description: { selector: ['description'] },
        heatTraceCircuitNumber: { selector: ['heat_trace_circuit_num'] },
        distributionPanel: { can: 'circuit-management', selector: ['distribution_panel'] },
        circuitBreaker: { can: 'circuit-management', selector: ['circuit_breaker'] },
        RTDTag: { can: 'circuit-management', selector: ['rtd_tag'] },
        locationUrl: { selector: ['latitude', 'longitude'], overrideElement: 'googleLink', can: 'geo-tagging' }
      },
      designedSettings: {
        deviceOperatingStatus: {
          selector: ['designed_values', 'deviceOperatingStatus'],
          overrideElement: 'selector',
          options: 'designedControlModes'
        },
        atcMode: {
          selector: ['designed_values', 'atcMode'],
          overrideElement: 'selector',
          options: 'designedAtcModeOptions'
        },
        maintainTemperature: {
          selector: ['designed_values', 'controlTemperatureSetpoint'],
          overrideElement: 'editTemperatureValueSelector'
        },
        lowTemperatureAlarm: {
          selector: ['designed_values', 'lowControlTemperatureAlarmSetpoint'],
          overrideElement: 'editTemperatureValueSelector'
        },
        highTemperatureAlarm: {
          selector: ['designed_values', 'highControlTemperatureAlarmSetpoint'],
          overrideElement: 'editTemperatureValueSelector'
        },
        lowLoadAlarm: { selector: ['designed_values', 'lowLoadCurrentAlarmSetpoint'], overrideElement: 'numericSelector' },
        highLoadAlarm: { selector: ['designed_values', 'highLoadCurrentAlarmSetpoint'], overrideElement: 'numericSelector' },
        groundFaultTripAlarmMask: { selector: ['designed_values', 'groundFaultTripAlarmMask'], overrideElement: 'booleanSelector' },
        gfTrip: { selector: ['designed_values', 'groundFaultTripCurrentSetpoint'], overrideElement: 'numericSelector' },
      },
      additionalData: {
        unit: { selector: ['unit'] },
        system: { selector: ['system'] },
        lineDescription: { selector: ['line_description'] },
        lineSize: { selector: ['line_size'] },
        lineNumber: { selector: ['line'] },
        commodity: { selector: ['commodity'] },
        pID: { selector: ['pnid'] },
        isoNumber: { selector: ['iso_number'] },
        heaterType: { selector: ['heater_type'] },
        maxSheathTemp: { selector: ['max_sheath_temp'], overrideElement: 'editTemperatureValueSelector' },
        maxExposureTemp: { selector: ['max_exposure_temp'], overrideElement: 'editTemperatureValueSelector' },
        ait: { selector: ['auto_ignition_temp'], overrideElement: 'editTemperatureValueSelector' },
        deadLeg: { selector: ['dead_legs'], overrideElement: 'booleanSelector' },
        liftRequired: { selector: ['lift_required'], overrideElement: 'triStateBooleanSelector' },
        priority: { selector: ['priority'], overrideElement: 'numericSelector' },
        voltage: { selector: ['voltage'], overrideElement: 'numericSelector' },
        location: { selector: ['location'] },
        moreButton: { selector: ['extendedProperties'], overrideElement: 'actionButton' },
      },
      properties: {
        offline: { selector: ['offline'], overrideElement: 'offlineSelector' },
        ipAddress: { selector: ['ip_address'], overrideElement: 'readOnly' },
        port: { selector: ['port'], overrideElement: 'readOnly' },
        serialPort: { selector: ['serial_port'], overrideElement: 'readOnly' },
        baudRate: { selector: ['baud_rate'], overrideElement: 'readOnly' },
        serialFormat: { selector: ['data_bits'], overrideElement: 'serialFormat' },
        htcAddress: { selector: ['device_address'], overrideElement: 'formatHtcAddress' },
        modbusAddress: { selector: ['address'], overrideElement: 'readOnlyCircuits' },
        subaddress: { selector: ['subaddress'], overrideElement: 'readOnlyCircuits' },
        commLoop: {
          selector: ['comm_loop_id'],
          overrideElement: 'commLoopForCircuit',
          options: 'commLoops'
        },
        firmwareVersion: { selector: ['firmware_version'], overrideElement: 'readOnly' }
      }
    },
    currentAlarms: true,
    gauges: true,
    showPrimaryGaugesByDefault: true,
    statistics: true,
    sensorGraph: true,
    currentNotifications: true,
    currentNotes: true,
    pastAlarms: true,
    pastNotifications: true,
    pastNotes: true,
    auditTrail: true,
    forcedOnLogs: true,
    fetchDeviceConfigFields: true,
    fetchDeviceActions: true,
    fetchDeviceStatus: true,
    activelyMonitored: true,
    testingReports: true,
    spliceReports: true,
    circuitFunctionTestReports: true,
    insulationInspectionTestReports: true,
    commissioningCircuitReports: true,
    commissioningCablePreInsulationReports: true,
    commissioningCablePostInsulationReports: true,
    ehtCircuitAuditReports: true,
    actionButtons: {
      createForcedOnLogs: true,
      advancedSettings: true,
      createNote: true,
      customActionButton: true,
      fileUpload: true,
      deleteDevice: {
        getMessage: circuitDeleteMessage,
        onSubmit: 'deleteCircuit',
        label: 'deviceType.deleteCircuit'
      }
    }

  },
  controller: {
    title: 'deviceType.controller',
    properties: {
      EHTInformation: {
        controllerTag: { selector: ['tag'] },
        comm_Loop: { selector: ['parent_tag'], overrideElement: 'deviceLink' },
        Model: { selector: ['device_factory'], overrideElement: 'readOnly' },
        locationUrl: { selector: ['latitude', 'longitude'], overrideElement: 'googleLink', can: 'geo-tagging' },
      },
      additionalData: {
        unit: { selector: ['unit'] },
        location: { selector: ['location'] },
        offline: { selector: ['offline'], overrideElement: 'offlineSelector' }
      },
      properties: {
        ipAddress: { selector: ['ip_address'],  overrideElement: 'readOnly' },
        port: { selector: ['port'],  overrideElement: 'readOnly' },
        serialPort: { selector: ['serial_port'], overrideElement: 'readOnly' },
        baudRate: { selector: ['baud_rate'], overrideElement: 'readOnly' },
        serialFormat: { selector: ['data_bits'], overrideElement: 'serialFormat' },
        modbusAddress: { selector: ['address'] },
        commLoop: {
          selector: ['comm_loop_id'],
          overrideElement: 'selector',
          options: 'commLoops'
        },
        firmwareVersion: { selector: ['firmware_version'], overrideElement: 'readOnly' },
      }
    },
    children: {
      title: 'deviceType.circuitsTitle',
      columnTagLabel: 'deviceType.circuitTagLabel',
      deleteMessage: 'deviceType.deleteMessageCircuit',
    },
    gauges: true,
    currentAlarms: true,
    currentNotifications: true,
    currentNotes: true,
    pastAlarms: true,
    pastNotifications: true,
    pastNotes: true,
    auditTrail: true,
    fetchDeviceStatus: true,
    fetchDeviceConfigFields: true,
    fetchDeviceActions: true,
    commissioningControllerReports: true,
    hmsAuditReports: true,
    ehtInsulationAuditReports: true,
    panelAuditReports: true,
    actionButtons: {
      advancedSettings: true,
      createNote: true,
      customActionButton: true,
      addCircuit: true,
      fileUpload: true,
      deleteDevice: {
        getMessage: controllerDeleteMessage,
        onSubmit: 'deleteController',
        label: 'deviceType.deleteController'
      }
    }
  },
  commLoop: {
    title: 'deviceType.commLoop',
    properties: {
      EHTInformation: { commLoopTag: { selector: ['tag'] } },
      properties: {
        ipAddress: { selector: ['ip_address'] },
        port: { selector: ['port'] },
        serialPort: { selector: ['serial_port'] },
        baudRate: { selector: ['baud_rate'], overrideElement: 'selector', options: 'baudRateOptions' },
        dataBits: { selector: ['data_bits'] },
        parity: { selector: ['parity'], overrideElement: 'selector', options: 'parityOptions' },
        stopBits: { selector: ['stop_bits'] },
        protocol: { selector: ['protocol'], overrideElement: 'selector', options: 'protocolOptions' },
        slowCommsMode: {
          selector: ['slow_communications_mode'],
          overrideElement: 'booleanSelector'
        },
        statusRefreshRate: { selector: ['polling_interval_status_factor'] },
        alarmRefreshRate: { selector: ['polling_interval_alarms_factor'] },
        settingsRefreshRate: { selector: ['polling_interval_settings_factor'] },
        assignedSupervisorClient: {
          selector: ['frog_client_id'],
          overrideElement: 'selector',
          options: 'frogClients'
        }
      }
    },
    children: {
      title: 'deviceType.devicesTitle',
      columnTagLabel: 'deviceType.deviceTagLabel',
      deleteMessage: 'deviceType.deleteMessageDevice',
      selector: []
    },
    currentAlarms: true,
    currentNotifications: true,
    currentNotes: true,
    pastAlarms: true,
    pastNotifications: true,
    pastNotes: true,
    deviceBreakDown: true,
    commissioningCommLoopReports: true,
    commissioningCablePreInsulationReports: true,
    commissioningCablePostInsulationReports: true,
    actionButtons: {
      createNote: true,
      fileUpload: true,
      deleteDevice: {
        getMessage: commLoopDeleteMessage,
        onSubmit: 'deleteCommLoop',
        label: 'deviceType.deleteCommLoop'
      }
    }
  }
};
